import * as Actions from '../actions/entityTimestampActions';
import EntityTimestamp from '../../entities/entityTimestamp';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {getSessionFirmId, getSessionUserId} from '../../lib/picos/sessionIdentity';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type EntityTimestampAction = {
    type: string;
    entityTimestamp: EntityTimestamp | null; 
    entityTimestampId: string | EntityTimestamp; 
    partial: boolean; 
    lastFetch: number; 
    entityTimestamps: EntityTimestamp[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<EntityTimestamp> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {EntityTimestamp[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<EntityTimestamp> | null, firmId: string, userId: string, updates?: EntityTimestamp[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'entityTimestamp', 'entityTimestamps', data ? backupRows(data) : null, updates)
		  .then(result => {
			  // OWA-786 (CXR-4-001): the worker resolves a typed result for every
			  // path — a resolution is NOT an acknowledgement. Only ok: true means
			  // the backup write is durable; anything else is surfaced, never
			  // swallowed, so a skipped or failed write cannot masquerade as one.
			  if (!result?.ok) console.warn(`⮂ entityTimestampReducer: backup write for 'entityTimestamps' did not persist (${result?.reason ?? 'unknown'}): ${result?.message ?? ''}`);
		  })
		  .catch(console.error);
}

/**
 * The identity that names this tab's backup store, or null when this tab has
 * not finished authenticating.
 *
 * OWA-785: this must come from sessionStorage (per tab), never from
 * localStorage (shared by every tab on the origin). With two firms open in two
 * tabs, the localStorage value is whichever firm logged in last, so the other
 * tab persisted its entities into that firm's backup store and they restored
 * into the wrong firm's UI. A partial identity names some other store, so we
 * skip the write rather than misfile it.
 *
 * @returns {{firmId: string, userId: string} | null}
 */
function storageIdentity(): {firmId: string; userId: string} | null {
	const firmId = getSessionFirmId();
	const userId = getSessionUserId();

	if (!firmId || !userId) {
		if (logging) console.debug('⮂ entityTimestampReducer: no session identity, skipping backup write');
		return null;
	}

	return {firmId, userId};
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<EntityTimestamp>} data
 * @param {EntityTimestamp[]} [updates]
 * @returns {ArrayMap<EntityTimestamp>}
 */
function updateStorage(data: ArrayMap<EntityTimestamp>, updates?: EntityTimestamp[]): ArrayMap<EntityTimestamp> {
	if (logging) console.debug('⮂ entityTimestampReducer: updateStorage');

	if (updates && updates.length === 0) return data;

	const identity = storageIdentity();
	if (!identity) return data;

	if (updates) callStorageWorker(null, identity.firmId, identity.userId, updates);
	else callStorageWorker(data, identity.firmId, identity.userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {EntityTimestamp[]} [updates]
 * @returns {ArrayMap<EntityTimestamp>}
 */
function updateStorageNoReturn(updates?: EntityTimestamp[]): void {
	if (logging) console.debug('⮂ entityTimestampReducer: updateStorageNoReturn');

	if (!updates || updates.length === 0) return;

	const identity = storageIdentity();
	if (!identity) return;

	callStorageWorker(null, identity.firmId, identity.userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {EntityTimestamp[]} [data]
 * @returns {ArrayMap<EntityTimestamp>}
 */
function createState(data?: EntityTimestamp[] | ArrayMap<EntityTimestamp>): ArrayMap<EntityTimestamp> {
	if (!data) return ArrayMapRegistry.clone<EntityTimestamp>('entityTimestamp', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<EntityTimestamp>('entityTimestamp', data.map(e => e));
    else return ArrayMapRegistry.clone<EntityTimestamp>('entityTimestamp', data);
}

// Whether deleted entityTimestamps keep their rows (schema includeDeletedInResults). OWA-68
const retainDeleted = false;

/**
 * The full backup snapshot of a collection: one entry per ID. A row when there is one (deleted rows of a
 * type that keeps them included); otherwise a deletion record for an ID whose row was removed. OWA-68
 *
 * @param {ArrayMap<EntityTimestamp>} data
 * @returns {Array<EntityTimestamp | EntityTimestampDeletionRecord>}
 */
function backupRows(data: ArrayMap<EntityTimestamp>): Array<EntityTimestamp | EntityTimestampDeletionRecord> {
	const records: EntityTimestampDeletionRecord[] = data.deletedVersionEntries()
		.filter(([id]) => !data.has(id))
		.map(([id, addedOn]) => ({entityTimestampId: id, addedOn, status: 'D', deletionRecord: true}));

	return records.length ? [...data.values(), ...records] : data.values();
}

/**
 * A deletion record read back from the offline backup: evidence that a entityTimestamp was deleted at a
 * version, never a live row. The storage worker writes one for a deleted entityTimestamp whose type does
 * not keep deleted rows. OWA-68
 */
type EntityTimestampDeletionRecord = {entityTimestampId: string; addedOn: number; status: 'D'; deletionRecord: true};

const isDeletionRecord = (o: unknown): o is EntityTimestampDeletionRecord => (o as EntityTimestampDeletionRecord)?.deletionRecord === true;

/**
 * Apply one incoming version of a entityTimestamp to a collection the caller owns (a clone), under the one
 * acceptance rule every path uses (OWA-68):
 * - older than the stored row: rejected
 * - an active version equal to a kept deleted row: rejected (it yields only to a strictly newer one)
 * - no stored row, and at or below the recorded deletion version: rejected
 * - an accepted deleted version records its deletion version; the row is kept as a deleted row when
 *   this type keeps deleted rows, otherwise it is removed
 * - an accepted active version is stored (a newer one supersedes the deletion version)
 *
 * @param {ArrayMap<EntityTimestamp>} st
 * @param {EntityTimestamp | EntityTimestampDeletionRecord} incoming
 * @returns {boolean} whether the version was accepted; only accepted versions go to the backup
 */
function applyVersion(st: ArrayMap<EntityTimestamp>, incoming: EntityTimestamp | EntityTimestampDeletionRecord): boolean {
	const id = isDeletionRecord(incoming) ? incoming.entityTimestampId : incoming.id;
	const current = st.get(id);

	if (current) {
		if (incoming.addedOn < current.addedOn) return false;
		// A kept deleted row yields only to a strictly newer active version, like a deletion version
		if (current.status === 'D' && incoming.status !== 'D' && incoming.addedOn === current.addedOn) return false;
	}
	else {
		const deletedAt = st.deletedVersion(id);
		if (deletedAt !== undefined && incoming.addedOn <= deletedAt) return false;
	}

	if (incoming.status === 'D') {
		st.recordDeletion(id, incoming.addedOn);
		if (retainDeleted && !isDeletionRecord(incoming)) st.set(incoming);
		else if (current) st.delete(current);
	}
	else st.set(incoming as EntityTimestamp);

	return true;
}

/**
 * A new indexed collection holding the rows and deletion versions of the given one. Fetch paths build
 * from this instead of cloning, because the initial store state is a bare ArrayMap without the
 * schema's indexes. OWA-68
 *
 * @param {ArrayMap<EntityTimestamp>} from
 * @returns {ArrayMap<EntityTimestamp>}
 */
function carryOver(from: ArrayMap<EntityTimestamp>): ArrayMap<EntityTimestamp> {
	const next = createState(from);
	from.deletedVersionEntries().forEach(([id, version]) => next.recordDeletion(id, version));
	return next;
}

function entityTimestamps(state: ArrayMap<EntityTimestamp>, action: EntityTimestampAction): ArrayMap<EntityTimestamp> {
	if (!state) state = createState();

	let st: ArrayMap<EntityTimestamp>;

	switch (action.type) {
		// action.entityTimestamp - the new value of entityTimestamp with id action.id
		// action.entityTimestampId - id of the target, used if new value is null
		case Actions.ENTITYTIMESTAMP_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ entityTimestampReducer: FETCH_ONE_SUCCESS', action);

			if (action.entityTimestamp === null) return state;

			// Only for a entityTimestamp this collection knows (a row, or a deletion version), and only when the
			// fetched version is accepted (OWA-68).
			if (state.has(action.entityTimestampId) || state.deletedVersion(action.entityTimestamp.id) !== undefined) {
				st = state.clone();
				if (!applyVersion(st, action.entityTimestamp)) return state;

				updateStorageNoReturn([action.entityTimestamp]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.entityTimestamps
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.entityTimestamps
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.entityTimestamps
			 !partial, !stateEmpty, fetchEmpty	-> D return entityTimestamps in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge entityTimestamps in state newer than those in action.entityTimestamps
		 */
		case Actions.ENTITYTIMESTAMP_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.entityTimestamps.length === 0;
			// A deletion record from the backup is evidence only: never constructed into an entity (OWA-68)
			const fetched: Array<EntityTimestamp | EntityTimestampDeletionRecord> = action.entityTimestamps.map(entityTimestamp => isDeletionRecord(entityTimestamp) ? entityTimestamp : new EntityTimestamp({...entityTimestamp, persisted: true}));
			const fetchedIds = new Set(fetched.map(entityTimestamp => isDeletionRecord(entityTimestamp) ? entityTimestamp.entityTimestampId : entityTimestamp.id));

			// A full fetch is the whole dataset, so a persisted active row it omits is dropped (deleted rows,
			// deletion versions and unsaved local rows stay). OWA-68
			const dropOmitted = (st: ArrayMap<EntityTimestamp>) => {
				[...st.values()].forEach(row => {
					if (!fetchedIds.has(row.id) && row.persisted && row.status !== 'D') st.delete(row);
				});
			};

			if (logging) console.debug(`⮂ entityTimestampReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} entityTimestamps with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ entityTimestampReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Build a new indexed state that keeps any deletion versions (the visible state is
					// empty, but a deletion version can still reject an older row). OWA-68
					st = carryOver(state);
					fetched.forEach(entityTimestamp => applyVersion(st, entityTimestamp));

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ entityTimestampReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<EntityTimestamp> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: EntityTimestamp[] = [];

				// Partials include deleted rows for every entity; applyVersion keeps or removes them and
				// records their deletion version (OWA-68). Only accepted versions go to the backup.
				fetched.forEach(entityTimestamp => {
					if (applyVersion(st, entityTimestamp)) toUpdate.push(entityTimestamp as EntityTimestamp);
					else if (logging) console.debug('⮂ entityTimestampReducer: partialMerge: rejected older version of entityTimestamp');
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ entityTimestampReducer: empty: entityTimestamps', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Drop the persisted active rows; deleted rows, deletion versions and unsaved local rows
				// stay (OWA-68)
				st = carryOver(state);
				dropOmitted(st);

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ entityTimestampReducer: fullMerge: entityTimestamps', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Start from the current rows and deletion versions, apply each fetched version under the one
				// rule, then drop persisted active rows the full fetch omitted (OWA-68). A fetched row replaces
				// a local one unless the local one is strictly newer, so a stale local (persisted or not) never
				// shadows a newer server row (OWA-7 #1), and a newer row that arrived while the fetch was in
				// flight (for example a socket update during startup restore) is kept.
				st = carryOver(state);
				fetched.forEach(entityTimestamp => applyVersion(st, entityTimestamp));
				dropOmitted(st);

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.ENTITYTIMESTAMP_FETCH_ONE_FAILURE:
			console.error('ENTITYTIMESTAMP_FETCH_ONE_FAILURE', action.entityTimestampId, action.err);

			// No update to storage required
			return state;

		case Actions.ENTITYTIMESTAMP_FETCH_FAILURE:
			console.error('ENTITYTIMESTAMP_FETCH_FAILURE', action.entityTimestampId, action.err);

			// No update to storage required
			return state;

		case Actions.ENTITYTIMESTAMP_RESTORE: {
		    let entityTimestamp = new EntityTimestamp({...action.entityTimestamp});
            st = state.clone();

            // One acceptance rule for every incoming version (OWA-68)
            if (!applyVersion(st, entityTimestamp)) return state;

            return updateStorage(st, [entityTimestamp]);
        }
            
		case Actions.ENTITYTIMESTAMP_SAVE: {
		    // console.debug('⮂ ENTITYTIMESTAMP_SAVE reducer', action.entityTimestamp?.id);
		    
			// clone the entityTimestamp
			let entityTimestamp = new EntityTimestamp({...action.entityTimestamp});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new entityTimestamp in the state, under the one acceptance rule (OWA-68). A rejected
			// version changes nothing and is not written to the backup.
			if (!applyVersion(st, entityTimestamp)) return state;

			return updateStorage(st, [entityTimestamp]);
		}

		case Actions.ENTITYTIMESTAMP_SAVE_MANY: {
		    if (logging) console.debug('⮂ ENTITYTIMESTAMP_SAVE_MANY reducer', action.entityTimestamps.map(entityTimestamp => entityTimestamp.id).join(','));
		    
			// clone the entityTimestamps
			let toProcess = action.entityTimestamps.map(entityTimestamp => new EntityTimestamp(entityTimestamp));

			st = state.clone();
			// state.clear();

			const updates: EntityTimestamp[] = [];

			toProcess.forEach(entityTimestamp => {
				// Add or update the new entityTimestamp in the state, under the one acceptance rule (OWA-68)
				if (applyVersion(st, entityTimestamp)) updates.push(entityTimestamp);
				else if (logging) console.debug('⮂ ENTITYTIMESTAMP_SAVE_MANY: rejected older version of entityTimestamp');
			});

			return updateStorage(st, updates);
		}

		case Actions.ENTITYTIMESTAMP_REMOVE: {
			// clone the entityTimestamp; a remove is a deleted version (the remove saga stamps it 'D' and a new
			// addedOn)
			const entityTimestamp = new EntityTimestamp({...action.entityTimestamp, status: 'D'});

			// Remove from UI under the one acceptance rule: the deletion version is recorded, and the row
			// is removed, or kept as a deleted row for a type that keeps them (OWA-68)
			st = state.clone();
			if (!applyVersion(st, entityTimestamp)) return state;

			return updateStorage(st, [entityTimestamp]);
		}

		case Actions.ENTITYTIMESTAMP_RESTORE_FAILURE:
		case Actions.ENTITYTIMESTAMP_SAVE_FAILURE: {
		    console.warn('ENTITYTIMESTAMP_SAVE_FAILURE reducer', action.entityTimestamp?.id);
		    if (!action.entityTimestamp?.id) {
				console.warn('ENTITYTIMESTAMP_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const entityTimestamp = ensure(EntityTimestamp, action.entityTimestamp);
			if (!entityTimestamp) {
				console.error('Could not find entityTimestamp after call to save');
				return state;
			}

			st = state.clone();

			const _entityTimestampRef = state.get(entityTimestamp.id);

			if (_entityTimestampRef && (_entityTimestampRef.addedOn === entityTimestamp.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _entityTimestamp = new EntityTimestamp({..._entityTimestampRef});
				_entityTimestamp.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_entityTimestamp.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.update('entityTimestamp', entityTimestamp.addedOn, {fatalError: _entityTimestamp.fatalError}).then(r => {
						if (r.isErr()) console.error(`⮂ entityTimestampReducer: failed to save entityTimestamp with fatalError info: ${r.unwrapErr().message}`);
						else if (r.unwrap() === 0) {
							// Non-document 4xx handlers may remove the row before dispatch.
							// Preserve the historical rejected-operation queue behavior.
							idb.add('entityTimestamp', _entityTimestamp).then(saved => {
								if (saved.isErr()) console.error('Could not restore rejected entityTimestamp: ' + saved.unwrapErr().message);
							}).catch(err => console.error('Could not restore rejected entityTimestamp: ' + err.message));
						}
						else console.debug('⮂ entityTimestampReducer: saved entityTimestamp with fatalError info');
					}).catch(err => console.error(`⮂ entityTimestampReducer: failed to save entityTimestamp with fatalError info: ${err.message}`));

				st.set(_entityTimestamp);
			}

			return updateStorage(st);
		}

		case Actions.ENTITYTIMESTAMP_SAVE_MANY_FAILURE: {
		    console.warn('ENTITYTIMESTAMP_SAVE_MANY_FAILURE reducer', action.entityTimestamps.map(entityTimestamp => entityTimestamp.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.ENTITYTIMESTAMP_RESTORE_SUCCESS:
		case Actions.ENTITYTIMESTAMP_SAVE_SUCCESS: {
			if (!action.entityTimestamp?.id) {
				console.warn('ENTITYTIMESTAMP_SAVE_SUCCESS reducer');
                return state;
			}

			const entityTimestampRef = state.get(action.entityTimestamp?.id);
			if (!entityTimestampRef) {
				console.warn('Could not find entityTimestamp after call to save');
                return state;
			}

			st = state.clone();

			// The echoed version goes through the one acceptance rule (OWA-68). When it is older than the
			// current row, only the current row's persisted flag changes, and the current row (never the
			// rejected echo) is what the backup receives.
			if (applyVersion(st, action.entityTimestamp)) return updateStorage(st, [action.entityTimestamp]);

			if (entityTimestampRef.persisted) return state;

			// Create a new instance to avoid mutating the entity shared with old state
			const entityTimestamp = new EntityTimestamp({...entityTimestampRef, persisted: true});
			st.set(entityTimestamp);

			return updateStorage(st, [entityTimestamp]);
		}

		case Actions.ENTITYTIMESTAMP_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ ENTITYTIMESTAMP_SAVE_MANY_SUCCESS reducer', action.entityTimestamps.map(entityTimestamp => entityTimestamp.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const entityTimestamps: EntityTimestamp[] = [];

			action.entityTimestamps.forEach(e => {
				const entityTimestampRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!entityTimestampRef) console.warn('Could not find entityTimestamp after call to save');
				// The echoed version goes through the one acceptance rule (OWA-68)
				else if (applyVersion(st, e)) entityTimestamps.push(e);
				else if (!entityTimestampRef.persisted) {
					// Older echo: only the current row's persisted flag changes, and the current row is
					// what the backup receives. Create a new instance to avoid mutating the entity shared
					// with old state.
					const entityTimestamp = new EntityTimestamp({...entityTimestampRef, persisted: true});
					st.set(entityTimestamp);
					entityTimestamps.push(entityTimestamp);
				}
			});

			return updateStorage(st, entityTimestamps);
		}

		case Actions.ENTITYTIMESTAMP_REMOVE_FAILURE: {
		    console.warn('ENTITYTIMESTAMP_REMOVE_FAILURE action', action.entityTimestamp?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.ENTITYTIMESTAMP_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.ENTITYTIMESTAMP_RESTORE_MANY: {
			if (logging) console.debug('⮂ ENTITYTIMESTAMP_RESTORE_MANY reducer', action.entityTimestamps.map(entityTimestamp => entityTimestamp.id).join(','));

			const toProcess = action.entityTimestamps.map(entityTimestamp => new EntityTimestamp({...entityTimestamp}));
			st = state.clone();

			const updates: EntityTimestamp[] = [];

			// One acceptance rule for every incoming version (OWA-68): a deleted version removes the row and
			// records its deletion version, or keeps a deleted row for a type that keeps them.
			toProcess.forEach(entityTimestamp => {
				if (applyVersion(st, entityTimestamp)) updates.push(entityTimestamp);
				else if (logging) console.debug('⮂ ENTITYTIMESTAMP_RESTORE_MANY: rejected older version of entityTimestamp');
			});

			return updateStorage(st, updates);
		}

		case Actions.ENTITYTIMESTAMP_REMOVE_MANY: {
			if (logging) console.debug('⮂ ENTITYTIMESTAMP_REMOVE_MANY reducer', action.entityTimestamps.map(entityTimestamp => entityTimestamp.id).join(','));

			// A socket delete is a deleted version, under the one acceptance rule (OWA-68)
			const toProcess = action.entityTimestamps.map(entityTimestamp => new EntityTimestamp({...entityTimestamp, status: 'D'}));
			st = state.clone();

			const updates: EntityTimestamp[] = [];

			toProcess.forEach(entityTimestamp => {
				if (applyVersion(st, entityTimestamp)) updates.push(entityTimestamp);
				else if (logging) console.debug('⮂ ENTITYTIMESTAMP_REMOVE_MANY: rejected older version of entityTimestamp');
			});

			return updateStorage(st, updates);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default entityTimestamps;